import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';

import NotFoundPage from './NotFoundPage';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import AnimatedSection from '../components/AnimatedSection';
import { FaBriefcase, FaImages } from 'react-icons/fa';
import './JurusanDetailPage.css';

// Import Swiper dan Lightbox
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions"; import Zoom from "yet-another-react-lightbox/plugins/zoom";
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import "yet-another-react-lightbox/styles.css";

const JurusanDetailPage = () => {
  const { id } = useParams();
  const [major, setMajor] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchMajor = async () => {
      setLoading(true);
      setError(false);
      try {
        const docRef = doc(db, 'majors', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const majorData = { id: docSnap.id, ...docSnap.data() };
          setMajor(majorData);

          // Jika ada galeri tertaut, ambil gambar-gambarnya
          if (majorData.linkedGallery) {
            const categoryQuery = query(collection(db, 'galeri_kategori'), where("slug", "==", majorData.linkedGallery));
            const categorySnapshot = await getDocs(categoryQuery);
            if (!categorySnapshot.empty) {
              const categoryName = categorySnapshot.docs[0].data().name;
              const imagesQuery = query(collection(db, 'galeri'), where("category", "==", categoryName), orderBy("order", "asc"));
              const imagesSnapshot = await getDocs(imagesQuery);
              const imagesData = imagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              setGalleryImages(imagesData);
            }
          }

        } else {
          console.error("No such document!");
          setError(true);
        }
      } catch (err) {
        console.error("Error fetching major document:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMajor();
  }, [id]);

  // State dan fungsi untuk Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const openLightbox = (index) => {
    setImageIndex(index);
    setLightboxOpen(true);
  };

  if (error || !major) {
    return <NotFoundPage />;
  }

  return (
    <>
      <SEO
        title={major ? `${major.title} - Program Keahlian` : "Program Keahlian"}
        description={major ? major.description : "Detail program keahlian di SMK Pariwisata Sumbangsih."}
      />
      {loading ? (
        <section className="section">
          <div className="container" style={{ textAlign: 'center', padding: '4rem 0' }}>
            Memuat detail program keahlian...
          </div>
        </section>
      ) : !major ? (
        <NotFoundPage />
      ) : (
        <>
          <PageHeader title={major.title} subtitle="Detail Program Keahlian" />

          <AnimatedSection>
            <section className="section">
              <div className="container jurusan-detail-container">
                <img src={major.image} alt={major.title} className="jurusan-detail-image" />
                <div className="jurusan-detail-content">
                  <h2>Tentang Program Keahlian {major.title}</h2>
                  <div dangerouslySetInnerHTML={{ __html: major.fullDescription || '<p>Deskripsi lengkap belum tersedia.</p>' }} />
                </div>
              </div>
            </section>
          </AnimatedSection>

          {galleryImages.length > 0 && (
            <AnimatedSection>
              <section className="section">
                <div className="container">
                  <h3><FaImages /> Galeri Kegiatan {major.title}</h3>
                  <Swiper
                    modules={[Navigation, Pagination, A11y]}
                    spaceBetween={15}
                    slidesPerView={1}
                    navigation
                    pagination={{ clickable: true }}
                    breakpoints={{
                      640: { slidesPerView: 2, spaceBetween: 20 },
                      1024: { slidesPerView: 3, spaceBetween: 20 },
                    }}
                    className="berita-gallery-slider" // Menggunakan style yang sama dengan galeri berita
                  >
                    {galleryImages.map((image, index) => (
                      <SwiperSlide
                        key={image.id}
                        className="gallery-slider-item"
                        onClick={() => openLightbox(index)}
                      >
                        <img src={image.url} alt={image.title || `Galeri ${major.title}`} loading="lazy" />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </section>
            </AnimatedSection>
          )}

          {major.careerProspects && major.careerProspects.length > 0 && major.careerProspects[0].title && (
            <AnimatedSection>
              <section className="section career-prospects-section">
                <div className="container">
                  <h2>Prospek Karir</h2>
                  <p className="page-intro-text">
                    Lulusan dari program keahlian {major.title} memiliki berbagai peluang karir yang menjanjikan di industri terkait.
                  </p>
                  <div className="prospects-grid">
                    {major.careerProspects.map((prospect, index) => (
                      <div key={index} className="prospect-card">
                        <div className="prospect-icon"><FaBriefcase /></div>
                        <h3>{prospect.title}</h3>
                        {prospect.description && <p>{prospect.description}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </AnimatedSection>
          )}

          <AnimatedSection>
            <div className="container jurusan-detail-container" style={{ textAlign: 'center', padding: '60px 20px 40px' }}>
              <Link to="/jurusan" className="btn-hero" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                <span>&larr;</span> Kembali ke Semua Program Keahlian
              </Link>
            </div>
          </AnimatedSection>

          <Lightbox
            open={lightboxOpen}
            close={() => setLightboxOpen(false)}
            slides={galleryImages.map(img => ({ src: img.url, title: img.title, description: img.description }))}
            index={imageIndex}
            plugins={[Captions, Zoom]}
            click={(api) => api.toggleControls()}
            zoom={{
              scrollToZoom: true,
              maxZoomPixelRatio: 5,
              click: 0, // Menonaktifkan zoom saat klik tunggal
            }}
            animation={{ zoom: 500, swipe: 250 }}
          />
        </>
      )}
    </>
  );
};

export default JurusanDetailPage;